/**
 * Camera Utilities - Native iOS Camera Implementation
 * Uses HTML5 file input to avoid getUserMedia permission prompts
 * Prevents permission lockout issues for non-technical users
 *
 * Use this for modules where instant capture is not critical:
 * - Daily Reports
 * - Photo Gallery
 * - Equipment Issues
 * - Safety Reports
 *
 * For time-critical modules (Truck Tickets), use Camera component instead
 */

export interface CameraOptions {
  maxDimension?: number; // Maximum width/height (default: 1800px for OCR)
  quality?: number; // JPEG quality 0-1 (default: 0.85)
  multiple?: boolean; // Allow multiple photos (default: false)
}

/**
 * Compress an image file to reduce upload size
 */
export const compressImage = (file: File, options: CameraOptions = {}): Promise<File> => {
  const { maxDimension = 1800, quality = 0.85 } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onerror = () => reject(new Error('Failed to load image'));

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(img.src);

              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        } else {
          reject(new Error('Canvas context not available'));
        }
      } catch (error) {
        reject(error);
      }
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Open native camera and capture photo(s)
 * Uses file input to avoid getUserMedia permission prompts
 *
 * Note: On iOS, this will show "Use Photo" / "Retake" confirmation dialog
 * This is acceptable for non-time-critical photo capture
 *
 * @param onCapture Callback function that receives compressed image file(s)
 * @param options Camera and compression options
 * @returns Promise that resolves when capture is complete
 */
export const openNativeCamera = async (
  onCapture: (files: File | File[]) => void,
  options: CameraOptions = {}
): Promise<void> => {
  const { multiple = false } = options;

  return new Promise((resolve, reject) => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment'; // Use rear camera - required for direct camera access
      if (multiple) {
        input.multiple = true;
      }

      // iOS PWA fix: Input must be in DOM and positioned off-screen
      // Without this, iOS may show black screen or fail to open camera
      input.style.position = 'fixed';
      input.style.top = '-1000px';
      input.style.left = '-1000px';
      input.style.opacity = '0';
      input.style.pointerEvents = 'none';
      document.body.appendChild(input);

      // Cleanup function to remove input and stop camera
      const cleanup = () => {
        try {
          // Remove event listeners
          input.onchange = null;
          input.oncancel = null;

          // Remove from DOM
          if (input.parentNode) {
            input.parentNode.removeChild(input);
          }

          // Clear the input value to release camera resources
          input.value = '';
        } catch (err) {
          console.error('Error during cleanup:', err);
        }
      };

      input.onchange = async (e: Event) => {
        try {
          const target = e.target as HTMLInputElement;
          const files = Array.from(target.files || []);

          if (files.length === 0) {
            cleanup();
            resolve();
            return;
          }

          // Compress all images
          const compressed = await Promise.all(
            files.map(file => compressImage(file, options))
          );

          // Return single file or array based on multiple flag
          if (multiple) {
            onCapture(compressed);
          } else {
            onCapture(compressed[0]);
          }

          cleanup();
          resolve();
        } catch (error) {
          console.error('Error processing camera capture:', error);
          cleanup();
          reject(error);
        }
      };

      input.oncancel = () => {
        cleanup();
        resolve(); // User cancelled - not an error
      };

      // Small delay for iOS to register the element in DOM before clicking
      setTimeout(() => {
        input.click();
      }, 50);
    } catch (error) {
      console.error('Error opening camera:', error);
      reject(error);
    }
  });
};

/**
 * Convert File to base64 data URL for preview
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
